// ─────────────────────────────────────────────────────────────────────────────
// Mack · CALCULATOR — THE DECISION INTERFACE (owner, 2026-10-14). FRONT END ONLY.
//
// Pictures 1–2's composition (the original floor, CalculatorTab.tsx at 69d957a — its layout,
// tokens and primitives), filled with the LIVE evidence the evidence view shows (pictures 3–4,
// CalculatorTab.tsx at HEAD, kept untouched). Three layers:
//   1. decision summary — status banner, the sheet's three panels, the block vector, three tiles;
//   2. compact criterion rows — picture 2's two tables, one short row per criterion;
//   3. full provenance on demand — a row opens an inline drawer with every field, then History.
//
// HOW THE INFORMATION IS GOT DOES NOT CHANGE: the same two GETs as the evidence view, the same
// parameters — the calculator GET at mount, after a committed OFR notice, or on reconnect;
// history only when History is opened. Everything here starts after the response arrives (lib/mackCalculatorView.js:
// groups, counts and formats; never a score, never a state of its own).
//
// NO EXAMPLE DATA: none of the design's ratings, scores, tier values, block values or catalysts.
// Every value is the response's, or a pending sentence where Formula Pass 2 has not run.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import { C, MONO, SANS } from './tokens';
import { card, panelTitle, panelNote, outlinePill, microLabel, MiniBar } from './primitives';
import { CALCULATOR_SHEETS, CALCULATOR_FAMILIES, CalculatorEvidence, CalculatorProjection, EvidenceHistory,
  fetchCalculatorHistory } from '../../lib/mackCalculatorEvidence';
import { buildModel, CriterionView, ENVIRONMENTS, kvLines, stateLabel, SheetView, DrawerRow, observationView,
  criterionAllFields, codeWords, countsText, gapsText, readProjection, readableNumber } from '../../lib/mackCalculatorView';
import { readCalculator } from '../../lib/mackCalculatorRead';
import { useCalculatorRetainedRead } from '../../lib/useCalculatorRetainedRead';

const CSS = `
.mcd-grid3 { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 17px; align-items: stretch; }
.mcd-blocks { display: grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap: 17px; }
.mcd-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.mcd-table th { text-align: left; font-weight: 400; padding: 0 10px 11px 0; vertical-align: bottom; }
.mcd-table td { padding: 0 10px 0 0; vertical-align: middle; }
.mcd-row > td { border-top: 1px solid ${C.rule}; height: 50px; }
.mcd-row { cursor: pointer; }
.mcd-row:hover > td { background: rgba(232,150,60,0.035); }
.mcd-btn:focus, .mcd-btn:focus-visible, .mcd-row:focus-visible, .mcd-select:focus, .mcd-select:focus-visible, .mcd-root summary:focus { outline: 2px solid ${C.amberBright}; outline-offset: 2px; }
.mcd-drawer > td { border-top: 1px dashed ${C.cardBorder}; padding: 14px 0 18px; }
.mcd-dl { display: grid; grid-template-columns: minmax(120px, 170px) minmax(0,1fr); column-gap: 14px; row-gap: 6px; margin: 0; }
.mcd-kv { display: grid; grid-template-columns: minmax(150px, 320px) minmax(0,1fr); column-gap: 14px; row-gap: 3px; }
.mcd-kv > span { font-family: ${MONO}; font-size: 11.5px; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word; }
.mcd-groups { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px 22px; }
.mcd-col-ref, .mcd-col-upd, .mcd-col-hist, .mcd-col-block { }
@media (max-width: 1180px) { .mcd-blocks { grid-template-columns: repeat(3, minmax(0,1fr)); } }
@media (max-width: 1100px) { .mcd-col-upd { display: none; } }
@media (max-width: 980px) {
  .mcd-grid3 { grid-template-columns: 1fr; }
  .mcd-col-ref, .mcd-col-hist, .mcd-col-block { display: none; }
  .mcd-groups { grid-template-columns: 1fr; }
}
@media (max-width: 700px) { .mcd-blocks { grid-template-columns: repeat(2, minmax(0,1fr)); } .mcd-col-score { display: none; } }
@media (prefers-reduced-motion: reduce) { .mcd-root * { transition: none !important; animation: none !important; } }
`;

const mono = (size = 12.5, color: string = C.textSub): React.CSSProperties => ({ fontFamily: MONO, fontSize: size, color });
// lbl with the desktop's secondary token: small text must read at WCAG AA on the card.
const lbl: React.CSSProperties = { ...microLabel, color: C.textSub };
const pendingBadge: React.CSSProperties = {
  fontSize: 9.5, fontFamily: MONO, color: C.amber, border: `1px solid ${C.cardBorder}`, borderRadius: 4,
  padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap',
};

function KV({ lines }: { lines: [string, string][] }) {
  if (!lines.length) return <div style={mono(11.5)}>None</div>;
  return <div className="mcd-kv">{lines.map(([k, v], i) => <React.Fragment key={`${k}:${i}`}>
    <span style={{ color: C.textSub }}>{k}</span><span style={{ color: C.textSub }}>{v}</span>
  </React.Fragment>)}</div>;
}

// One named field: the words, and the exact stored value beside them in small mono.
function FieldRow({ r }: { r: DrawerRow }) {
  return <>
    <dt style={{ ...lbl, paddingTop: 2 }}>{r.label}</dt>
    <dd style={{ margin: 0, minWidth: 0 }}>
      {r.lines && r.lines.length ? r.lines.map((l) => <div key={l} style={{ ...mono(12, C.text), overflowWrap: 'anywhere' }}>{l}</div>)
        : r.href ? <a href={r.href} title={r.href} target="_blank" rel="noopener noreferrer" className="mcd-btn" style={{ color: C.amber, fontSize: 13, overflowWrap: 'anywhere' }}>{r.text}<span aria-hidden="true"> ↗</span></a>
        : <span style={{ fontSize: 13, color: r.text === '—' ? C.textSub : C.text, overflowWrap: 'anywhere' }}>{r.text}</span>}
      {r.exact && r.exact !== r.text && <span style={{ ...mono(10.5), marginLeft: 8, overflowWrap: 'anywhere' }}>{r.exact}</span>}
    </dd>
  </>;
}

function Fold({ title, lines }: { title: string; lines: [string, string][] }) {
  return <details style={{ marginTop: 12 }}>
    <summary className="mcd-btn" style={{ ...lbl, cursor: 'pointer', minHeight: 24 }}>{title} — {lines.length} stored values, exactly as received</summary>
    <div style={{ marginTop: 8 }}><KV lines={lines} /></div>
  </details>;
}

function HistoryList({ field, authToken }: { field: CalculatorEvidence; authToken?: string | null }) {
  const [history, setHistory] = useState<EvidenceHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  async function load(offset = 0) {
    setLoading(true); setError(null);
    try {
      const next = await fetchCalculatorHistory(field, authToken, offset);
      setHistory((previous) => (offset && previous ? { ...next, rows: [...previous.rows, ...next.rows] } : next));
    } catch { setError('History unavailable'); }
    finally { setLoading(false); }
  }
  const toggle = () => { const next = !open; setOpen(next); if (next && !history && !loading) load(); };
  const rows = history ? history.rows : [];
  return <div style={{ marginTop: 10 }}>
    <button type="button" className="mcd-btn" aria-expanded={open} onClick={toggle} style={{
      background: C.chipOffBg, border: `1px solid ${C.chipOffBd}`, color: C.amber, borderRadius: 6,
      padding: '6px 11px', fontFamily: MONO, fontSize: 11.5, cursor: 'pointer', minHeight: 32,
    }}>{open ? 'Hide history' : `History (${field.history_count})`}</button>
    <span style={{ ...mono(11), marginLeft: 10 }}>{codeWords(field.history_state)}</span>
    {open && <div style={{ marginTop: 10 }}>
      {loading && !history && <div style={mono(11.5)}>Loading retained history…</div>}
      {error && <div role="alert" style={mono(11.5, C.amber)}>{error}</div>}
      {history && <>
        <div style={{ ...mono(11), marginBottom: 6 }}>{history.total} retained · newest as the backend orders them · nothing interpolated or recalculated</div>
        <div style={{ maxHeight: 300, overflow: 'auto', border: `1px solid ${C.rule}`, borderRadius: 6 }}>
          <table className="mcd-table" style={{ fontSize: 11.5 }}>
            <caption style={{ position: 'absolute', left: -9999 }}>Retained history for {field.label}</caption>
            <thead><tr style={{ position: 'sticky', top: 0, background: C.card }}>
              {['Reference', 'Source evidence', 'Unit', 'Publication / provider update', 'Observed / retrieved', 'Window', 'Vintage']
                .map((h) => <th key={h} scope="col" style={{ ...lbl, padding: '8px 8px' }}>{h}</th>)}
            </tr></thead>
            <tbody>{rows.map((r, i) => {
              const raw = r.raw_record as Record<string, unknown> | undefined;
              const value = raw ? kvLines(raw).map(([k, v]) => `${k}: ${v}`).join(' · ')
                : r.source_native_value === null || r.source_native_value === undefined ? 'Unavailable' : String(r.source_native_value);
              return <tr key={String(r.material_hash || r.vintage_id || i)} style={{ borderTop: `1px solid ${C.rule}` }}>
                <td style={{ ...mono(11.5, C.text), padding: '6px 8px' }}>{String(r.reference_period ?? 'Unavailable')}</td>
                <td style={{ ...mono(11.5, C.text), padding: '6px 8px', overflowWrap: 'anywhere' }}>{value}</td>
                <td style={{ ...mono(11.5), padding: '6px 8px' }}>{String(r.unit ?? 'Unavailable')}</td>
                <td style={{ ...mono(11.5), padding: '6px 8px', overflowWrap: 'anywhere' }}>{String(r.publication_at ?? 'Not exposed')}</td>
                <td style={{ ...mono(11.5), padding: '6px 8px', overflowWrap: 'anywhere' }}>{String(r.observed_at || r.retrieved_at || 'Unavailable')}</td>
                <td style={{ ...mono(11.5), padding: '6px 8px', overflowWrap: 'anywhere' }}>{String(r.collection_window_id || 'Not applicable')}</td>
                <td style={{ ...mono(11), padding: '6px 8px', overflowWrap: 'anywhere' }}>{String(r.vintage_id || r.material_hash || 'Unavailable')}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        {history.next_offset !== null && <button type="button" className="mcd-btn" disabled={loading}
          onClick={() => load(history.next_offset as number)} style={{
            marginTop: 8, background: 'none', border: `1px solid ${C.chipOffBd}`, color: C.amber, borderRadius: 6,
            padding: '5px 10px', fontFamily: MONO, fontSize: 11, cursor: 'pointer', minHeight: 30,
          }}>{loading ? 'Loading…' : 'Load older history'}</button>}
      </>}
    </div>}
  </div>;
}

function Observation({ e, slot, role, authToken }: { e: CalculatorEvidence; slot: any; role: string; authToken?: string | null }) {
  const o = observationView(e, slot);
  return <div style={{ borderTop: `1px solid ${C.rule}`, paddingTop: 12, marginTop: 12 }} data-evidence-id={e.field_id}>
    <h4 style={{ margin: '0 0 10px', fontSize: 13.5, fontWeight: 700, color: C.text }}>
      <span style={{ ...lbl, marginRight: 8 }}>{role}</span>{o.title}
      <span style={{ ...mono(12.5, C.amber), marginLeft: 10 }}>{o.value}</span>
      <span style={{ fontSize: 12, color: C.textSub, marginLeft: 10 }}>{o.state}</span>
    </h4>
    <div className="mcd-groups">
      {o.sections.map((sec) => <section key={sec.title} aria-label={sec.title}>
        <h5 style={{ ...lbl, margin: '0 0 8px', color: C.amber }}>{sec.title}</h5>
        <dl className="mcd-dl">{sec.rows.map((r, i) => <FieldRow key={`${r.label}:${i}`} r={r} />)}</dl>
        {sec.history && (e.history_count > 0 || e.history_state) && <HistoryList key={`${e.field_id}:${e.retrieved_at}:${e.history_count}`} field={e} authToken={authToken} />}
      </section>)}
    </div>
    <Fold title="All fields" lines={o.allFields} />
  </div>;
}

function Drawer({ c, authToken }: { c: CriterionView; authToken?: string | null }) {
  const s = c.slot;
  return <div style={{ padding: '0 6px' }}>
    {(s.evidence || []).map((e: CalculatorEvidence) => <Observation key={e.field_id} e={e} slot={s} role="Observation" authToken={authToken} />)}
    {(s.supporting_evidence || []).map((e: CalculatorEvidence) => <Observation key={`s:${e.field_id}`} e={e} slot={s} role="Supporting" authToken={authToken} />)}
    {(s.calendar_evidence || []).map((e: CalculatorEvidence) => <Observation key={`c:${e.field_id}`} e={e} slot={s} role="Release evidence" authToken={authToken} />)}
    {!(s.evidence || []).length && <div style={{ fontSize: 13, color: C.textSub, marginTop: 8 }}>{stateLabel(s.formula_state)}</div>}
    <div style={{ borderTop: `1px solid ${C.rule}`, paddingTop: 10, marginTop: 12 }}>
      <Fold title="All fields — this criterion" lines={criterionAllFields(s)} />
    </div>
  </div>;
}

function CriteriaTable({ title, rows, authToken, open, setOpen }: {
  title: string; rows: CriterionView[]; authToken?: string | null;
  open: Record<string, boolean>; setOpen: (id: string) => void;
}) {
  const anyScore = rows.some((r) => r.score !== null && r.score !== undefined);
  return <section aria-label={title} style={{ marginBottom: 30 }}>
    <h3 style={{ ...lbl, margin: '0 0 15px', display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
      <span>{title} — {rows.length} inputs</span>
      {!anyScore && <span style={pendingBadge}>Score · Awaiting Formula Pass 2</span>}
    </h3>
    <table className="mcd-table">
      <colgroup>
        <col style={{ width: '20%' }} /><col className="mcd-col-block" style={{ width: '9%' }} /><col style={{ width: '20%' }} />
        <col style={{ width: '17%' }} /><col className="mcd-col-ref" style={{ width: '10%' }} /><col className="mcd-col-upd" style={{ width: '7%' }} />
        <col className="mcd-col-hist" style={{ width: '7%' }} /><col className="mcd-col-score" style={{ width: '6%' }} /><col style={{ width: '4%' }} />
      </colgroup>
      <thead><tr>
        <th scope="col" style={lbl}>Criterion</th>
        <th scope="col" className="mcd-col-block" style={lbl}>Block</th>
        <th scope="col" style={lbl}>Latest evidence</th>
        <th scope="col" style={lbl}>Evidence state</th>
        <th scope="col" className="mcd-col-ref" style={lbl}>Reference</th>
        <th scope="col" className="mcd-col-upd" style={lbl}>Updated</th>
        <th scope="col" className="mcd-col-hist" style={lbl}>History</th>
        <th scope="col" className="mcd-col-score" style={lbl}>
          Score
        </th>
        <th scope="col" style={lbl}><span style={{ position: 'absolute', left: -9999 }}>Details</span></th>
      </tr></thead>
      <tbody>{rows.map((c) => {
        const isOpen = !!open[c.id];
        const drawerId = `mcd-drawer-${c.id}`;
        return <React.Fragment key={c.id}>
          <tr className="mcd-row" data-criterion-id={c.id} onClick={() => setOpen(c.id)}>
            <td style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{c.name}</td>
            <td className="mcd-col-block" style={mono(12.5)}>{c.block}</td>
            <td style={{ ...mono(13, C.text), overflowWrap: 'anywhere' }}>
              {readableNumber(c.latest.value, c.latest.unit)}{c.latest.unit && <span style={{ ...mono(11.5), marginLeft: 6 }}>{c.latest.unit}</span>}
            </td>
            <td style={{ fontSize: 12.5, color: c.waiting || c.accessGap ? C.amber : C.textSub }}>{c.stateText}</td>
            <td className="mcd-col-ref" style={mono(12)}>{c.reference}</td>
            <td className="mcd-col-upd" style={mono(12)}>{c.updated}</td>
            <td className="mcd-col-hist" style={mono(12)}>{c.history}</td>
            <td className="mcd-col-score" style={mono(13, c.score === null || c.score === undefined ? C.textSub : C.amber)}>
              {c.score === null || c.score === undefined ? '—' : String(c.score)}
            </td>
            <td style={{ textAlign: 'right' }}>
              <button type="button" className="mcd-btn" aria-expanded={isOpen} aria-controls={drawerId}
                aria-label={`${isOpen ? 'Hide' : 'Show'} evidence for ${c.name}`}
                onClick={(ev) => { ev.stopPropagation(); setOpen(c.id); }} style={{
                  background: 'none', border: `1px solid ${C.chipOffBd}`, borderRadius: 6, color: C.amber,
                  width: 32, height: 32, cursor: 'pointer', fontFamily: MONO, fontSize: 13,
                }}>{isOpen ? '−' : '+'}</button>
            </td>
          </tr>
          {isOpen && <tr className="mcd-drawer" id={drawerId}><td colSpan={9}><Drawer c={c} authToken={authToken} /></td></tr>}
        </React.Fragment>;
      })}</tbody>
    </table>
  </section>;
}

function Readiness({ label, part, amber }: { label: string; part: { ready: number; total: number }; amber: boolean }) {
  return <div>
    <div style={lbl}>{label} <span style={{ color: C.amber }}>of {part.total}</span></div>
    <div style={{ fontSize: 30, fontWeight: 700, fontFamily: MONO, color: amber ? C.amber : C.text, margin: '6px 0 2px' }}>{part.ready}</div>
    <div style={{ ...mono(11), marginBottom: 9 }}>evidence ready</div>
    <MiniBar pct={part.total ? part.ready / part.total : 0} />
  </div>;
}

function SheetPanels({ v, sheets, sheet, onSheet }: { v: SheetView; sheets: string[]; sheet: string; onSheet: (s: string) => void }) {
  const panel: React.CSSProperties = { ...card, padding: '18px 20px' };
  const dests = Object.keys(v.downstream);
  return <div className="mcd-grid3">
    <section style={panel} aria-label="Country sheet summary">
      <h3 id="mcd-sheet-heading" style={{ ...lbl, margin: '0 0 10px' }}><label htmlFor="mcd-sheet">Country sheet</label></h3>
      <div style={{ position: 'relative' }}>
        <select id="mcd-sheet" className="mcd-select" value={sheet} onChange={(e) => onSheet(e.target.value)} style={{
          width: '100%', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', background: '#0a0807', color: C.text,
          border: `1px solid ${C.chipOffBd}`, borderRadius: 8, padding: '11px 34px 11px 14px', fontSize: 15, fontWeight: 700,
          fontFamily: SANS, cursor: 'pointer', minHeight: 44,
        }}>{sheets.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          fill="none" stroke={C.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 6 8 10.5 12.5 6" /></svg>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
        <Readiness label="Macro" part={v.macro} amber />
        <Readiness label="Sentiment" part={v.sentiment} amber={false} />
      </div>
      {v.unassigned.total > 0 && <div style={{ ...mono(12), marginTop: 10 }}>Unassigned · {v.unassigned.ready} of {v.unassigned.total} evidence ready</div>}
      <div style={{ height: 1, background: C.rule, margin: '18px 0 14px' }} />
      <div style={{ ...mono(12.5), lineHeight: 1.6 }}>
        {v.nextCatalyst ? v.nextCatalyst.text : 'No scheduled catalyst in the response'}
      </div>
      <div style={{ ...mono(11), marginTop: 6, letterSpacing: '0.06em' }}>READINESS COUNTS · NOT SCORES</div>
    </section>

    <section style={panel} aria-label="Environment from Phase">
      <h3 style={{ ...lbl, margin: '0 0 11px' }}>Environment — from Phase</h3>
      <ul style={{ display: 'flex', gap: 7, flexWrap: 'wrap', listStyle: 'none', margin: 0, padding: 0 }} aria-label="Environments (read-only)">
        {ENVIRONMENTS.map((e) => {
          const on = v.environment === e;
          return <li key={e} aria-current={on ? 'true' : undefined} style={{
            fontSize: 12, fontFamily: SANS, fontWeight: 600, padding: '6px 11px', borderRadius: 999,
            background: on ? C.chipBg : C.chipOffBg, color: on ? C.amberBright : C.textSub,
            border: `1px solid ${on ? C.chipBorder : C.chipOffBd}`,
          }}>{e}</li>;
        })}
      </ul>
      <div style={{ ...mono(12.5), marginTop: 13 }}>{v.environment ? `Current · ${v.environment}` : 'Awaiting Phases'}</div>
      <div style={{ height: 1, background: C.rule, margin: '18px 0 14px' }} />
      <div style={lbl}>Formula Pass 2</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '6px 0 2px' }}>{v.formulaStates.map(stateLabel).join(' · ')}</div>
      <div style={mono(11.5)}>{countsText(v.ready, v.waiting, v.total)}</div>
      <div style={{ ...mono(11), marginTop: 3 }}>{gapsText(v.accessGaps)}</div>
      <div style={{ ...lbl, marginTop: 14 }}>Horizon</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: v.horizon ? C.text : C.textSub, marginTop: 6 }}>{v.horizon || 'Awaiting Formula Pass 2'}</div>
    </section>

    <section style={{ ...panel, display: 'flex', flexDirection: 'column' }} aria-label="What this sheet routes to">
      <h3 style={{ ...lbl, margin: 0 }}>What this sheet routes to</h3>
      <div style={{ ...panelNote, marginTop: 10 }}>Downstream destinations. Tier values arrive with Formula Pass 2; each destination's note is in Response details.</div>
      <div style={{ marginTop: 10, flex: 1 }}>
        {dests.map((d, i) => <div key={d} style={{ padding: '10px 0', borderTop: i ? `1px solid ${C.rule}` : 'none', display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: C.text }}>{d}</div>
          <span style={{ ...mono(14, C.textSub), fontWeight: 700, minWidth: 30, textAlign: 'right' }}>—</span>
        </div>)}
        {!dests.length && <div style={mono(12)}>No downstream destination in the response</div>}
      </div>
    </section>
  </div>;
}

export function CalculatorDecisionView({ projection, authToken, now, notices }: {
  projection: CalculatorProjection | null; authToken?: string | null; now?: number; notices?: string[];
}) {
  const model = useMemo(() => buildModel(projection, now), [projection, now]);
  const [sheet, setSheet] = useState<string>('USD');
  const [open, setOpenMap] = useState<Record<string, boolean>>({});
  const current = model.sheets.includes(sheet) ? sheet : model.sheets[0];
  const v = current ? model.sheetView(current) : null;
  const toggle = (id: string) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  const b = model.banner;
  return <div className="mcd-root" style={{ display: 'flex', flexDirection: 'column', gap: 17, color: C.text, fontFamily: SANS }}>
    <style>{CSS}</style>

    {/* ── STATUS BANNER (picture 1's top band) ── */}
    <section style={{ ...card, padding: '19px 22px' }} aria-label="Calculator status">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ ...panelTitle, margin: 0 }}>Calculator — Evidence</h2>
          <div style={{ ...panelNote, marginTop: 8 }}>
            {b.sheets} sheets · {b.criteria} criteria · Source contracts <span style={{ fontFamily: MONO }}>{b.sourceContracts || '—'}</span>
            {' · '}{stateLabel('FORMULA_PASS2_PENDING')} · {b.formulaPending === null ? '—' : `${b.formulaPending} of ${b.formulaTotal}`}
            {' · '}Last evidence refresh {b.lastRefreshAge}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: C.text, marginTop: 14 }} data-counts>{countsText(b.ready, b.waiting, b.criteria)}</div>
          <div style={{ ...mono(11.5), marginTop: 8 }} data-gaps-note>{gapsText(b.accessGaps)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ ...outlinePill(C.violet), display: 'inline-block' }}>{b.chip}</span>
          {b.activation && <div style={{ fontSize: 12, color: C.textSub, marginTop: 8, maxWidth: 360 }}>{codeWords(b.activation)}</div>}
        </div>
      </div>
    </section>

    {!projection && <div role="status" style={mono(12.5, C.amber)}>Calculator evidence has not been read.</div>}
    {notices && notices.length > 0 && <section role="status" aria-label="Response notices" style={{ ...card, padding: '14px 20px', borderColor: C.amber }}>
      <h3 style={{ ...lbl, margin: '0 0 8px', color: C.amber }}>The response changed — shown as received</h3>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>{notices.map((n) => <li key={n} style={{ fontSize: 12.5, color: C.text }}>{n}</li>)}</ul>
    </section>}

    {v && <>
      <SheetPanels v={v} sheets={model.sheets} sheet={current} onSheet={setSheet} />

      {/* ── THE BLOCK VECTOR — readiness, not scores ── */}
      <section style={{ ...card, padding: '18px 22px 22px' }} aria-label="Block vector">
        <h3 style={{ ...lbl, margin: '0 0 14px' }}>The block vector — evidence readiness per block</h3>
        <div className="mcd-blocks">
          {v.blocks.map((k) => <div key={k.block} style={{ ...card, padding: '13px 14px 15px' }} data-block={k.block}>
            <div style={lbl}>{k.block}</div>
            <div style={{ fontSize: 21, fontWeight: 700, fontFamily: MONO, color: C.amber, margin: '8px 0 4px' }}>{k.ready}/{k.total}</div>
            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 11, minHeight: 32 }}>{k.state}</div>
            <MiniBar pct={k.total ? k.ready / k.total : 0} />
            {k.blocker && <div style={{ ...mono(11, C.amber), marginTop: 10, overflowWrap: 'anywhere' }}>{k.blocker}</div>}
          </div>)}
        </div>
        <div className="mcd-grid3" style={{ marginTop: 17 }}>
          {v.tiles.map((t) => <div key={t.key} style={{ ...card, padding: '14px 16px 16px' }}>
            <div style={lbl}>{t.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.value === null ? C.textSub : C.text, margin: '9px 0 5px' }}>
              {t.value === null ? t.pending : String(t.value)}
            </div>
          </div>)}
        </div>
      </section>

      {/* ── CRITERIA (picture 2's two tables) ── */}
      <section style={{ ...card, padding: '20px 26px 10px' }} aria-label={`${current} criteria`}>
        <CriteriaTable title="Macro criteria" rows={v.macro.rows} authToken={authToken} open={open} setOpen={toggle} />
        <CriteriaTable title="Sentiment criteria" rows={v.sentiment.rows} authToken={authToken} open={open} setOpen={toggle} />
        {v.unassigned.total > 0 && <CriteriaTable title="Unassigned" rows={v.unassigned.rows} authToken={authToken} open={open} setOpen={toggle} />}
      </section>
    </>}

    {projection && <details style={{ ...card, padding: '14px 22px' }}>
      <summary className="mcd-btn" style={{ ...lbl, cursor: 'pointer' }}>Response details — every field outside the criteria</summary>
      <div style={{ marginTop: 12 }}>
        <KV lines={Object.keys(projection).filter((k) => k !== 'slots').flatMap((k) => kvLines((projection as any)[k], k))} />
        {v && Object.keys(v.downstream).length > 0 && <>
          <h4 style={{ ...lbl, margin: '14px 0 6px' }}>Destinations — {current}</h4>
          <KV lines={Object.keys(v.downstream).flatMap((d) => v.downstream[d].map((t) => [d, t] as [string, string]))} />
        </>}
      </div>
    </details>}
  </div>;
}

export function CalculatorDecisionTab({ authToken }: { authToken?: string | null }) {
  const { value: read, error } = useCalculatorRetainedRead(readCalculator, authToken);
  const missing = error && !read
    ? readProjection(null, () => { throw new Error('no response'); }, CALCULATOR_SHEETS, CALCULATOR_FAMILIES) : null;
  const notices = [...(read?.notices || []), ...(error
    ? ['Calculator evidence could not be read from the server.' + (read ? ' Previously confirmed evidence remains visible.' : '')] : [])];
  return <CalculatorDecisionView projection={read ? read.projection : missing?.projection || null} notices={notices} authToken={authToken} />;
}
